import React, {
  FunctionComponent,
  useRef,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { IconName } from 'app/common/components/controls/icon';
import { AtlassianAppSwitcherPopover } from 'app/gamma/src/components/popovers/atlassian-app-switcher';
import { MemberModel } from 'app/gamma/src/types/models';
import API from 'app/gamma/src/api';
import HeaderButton from './button';
import { usePopover } from '../popover';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag, featureFlagClient } from '@trello/feature-flag-client';

enum AtlassianSwitcherVariations {
  CONTROL = 'control',
  NOT_ENROLLED = 'not-enrolled',
  EXPERIMENT = 'experiment',
}

interface AtlassianAppSwitcherButtonProps {
  member: MemberModel | undefined;
}

export const AtlassianAppSwitcherButton: FunctionComponent<AtlassianAppSwitcherButtonProps> = ({
  member,
}) => {
  const locale = member?.prefs?.locale;
  const isAaMastered = member?.isAaMastered;
  const switcherVariation = useFeatureFlag(
    'a4aa.atlassian-app-switcher',
    AtlassianSwitcherVariations.NOT_ENROLLED,
  );
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const appSwitcherMenuButtonRef = useRef<HTMLButtonElement>(null);
  const { isPopoverShown, showPopover } = usePopover({
    name: HeaderTestIds.AtlassianAppSwitcher,
  });

  const onClick = useCallback(() => {
    showPopover();
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianSwitcherHeaderButton',
      source: 'atlassianSwitcherHeaderButton',
    });
  }, [showPopover]);

  useEffect(() => {
    const checkShowAppSwitcher = async () => {
      // If the user isn't enrolled, do nothing.
      if (switcherVariation === AtlassianSwitcherVariations.NOT_ENROLLED) {
        return;
      }
      const supportedLanguages = ['en-US', 'en-AU', 'en-GB'];
      // If the user has an Atlassian Account and has an English locale...
      if (isAaMastered && locale && supportedLanguages.includes(locale)) {
        let data = null;
        try {
          data = await API.client.atlassianSwitcherData();
        } catch (e) {
          const attributes: { message: string; statusCode?: number } = {
            message: String(e),
          };
          if (typeof e === 'object' && typeof e.statusCode === 'number') {
            attributes.statusCode = e.statusCode;
          }
          Analytics.sendOperationalEvent({
            action: 'failed',
            actionSubject: 'atlassianSwitcherHeaderButton',
            attributes,
            source: 'atlassianSwitcherHeaderButton',
          });
        }
        // If the user has more than 1 site...
        if (data && data.sites && data.sites.length) {
          // Fire the exposure event.
          featureFlagClient.getTrackedVariation(
            'a4aa.atlassian-app-switcher',
            AtlassianSwitcherVariations.NOT_ENROLLED,
          );
          if (switcherVariation === AtlassianSwitcherVariations.EXPERIMENT) {
            setShowAppSwitcher(true);
          }
        }
      }
    };

    checkShowAppSwitcher();
  }, [switcherVariation, isAaMastered, locale]);

  return showAppSwitcher ? (
    <>
      <HeaderButton
        icon={IconName.AppSwitcher}
        onClick={onClick}
        ref={appSwitcherMenuButtonRef}
        testId={HeaderTestIds.AtlassianAppSwitcher}
      />
      {isPopoverShown && (
        <AtlassianAppSwitcherPopover
          target={() => appSwitcherMenuButtonRef.current}
        />
      )}
    </>
  ) : null;
};
